import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Icon,
  HStack,
  Button,
  Divider,
  Spinner,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Tooltip,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import { labelMatchesQuery } from "modules/chat/utils/chatUtils";
import { ArtifactProto } from "build-ts/chat_session";
import DataFrameDisplayComponent from "modules/artifacts/dataframe/containers/DataFrame";
import DataFrameAnalysisComponent from "modules/artifacts/dataframe/containers/DataFrameAnalysis";
import FileArtifactComponent from "modules/artifacts/file/containers/FileArtifact";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import { IconType } from "react-icons";
import { AppIcons } from "constants/icons";
import { ArtifactOpenOpts, SelectedArtifactItem } from "modules/interfaces";
import AnalysisOutputCard, { AnalysisOutputEntry } from "./workflow/AnalysisOutputCard";
import WorkflowCard from "./workflow/WorkflowCard";
import WorkflowRunCard from "./workflow/containers/WorkflowRunCard";
import WorkflowRunSearchCard from "./workflow/containers/WorkflowRunSearchCard";
import {
  ArtifactMap,
  getArtifactLabel,
  getArtifactIcon,
  getArtifactTabKey,
  buildArtifactSections,
} from "./utils/artifactUtils";
import { ScheduledJobProto } from "build-ts/schedule";
import { HeaderWithStats } from "services/MetadataServices";

type AnalysisOutputGroup = {
  groupId: string;
  items: SelectedArtifactItem[];
  entries: AnalysisOutputEntry[];
  minOrderIndex: number;
};

type ArtifactTab = {
  key: string;
  label: string;
  icon: IconType;
  sortKey: number;
  onClose: () => void;
};

interface ArtifactPanelProps {
  selectedArtifacts?: SelectedArtifactItem[];
  isLoadingArtifact?: boolean;
  onClosePanel?: () => void;
  onClearAll?: () => void;
  onRemoveArtifact?: (artifact: ArtifactProto) => void;
  /** Set of job header ids already persisted (used to show Saved vs Draft badge) */
  savedJobIds?: Set<string>;
  /** Populate a parent artifact in-place so the Inputs tab can render its data inline */
  onPopulateInput?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => Promise<void> | void;
  /** Signal to activate the tab for a specific orderIndex without moving it */
  activateArtifact?: { orderIndex: number; tick: number };
  /** Full artifact registry — iterated (deduplicated) to populate the browse menu */
  artifactMap?: ArtifactMap;
  /** Lookup from useArtifactStore — forwarded to InputsTab for type-checking */
  lookupFn?: (name: string) => ArtifactProto | undefined;
  /** Called when user picks an artifact or sub-resource from the browse menu */
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  /** Whether the artifact panel is currently expanded to full width */
  isArtifactExpanded?: boolean;
  /** Called when the expand/collapse toggle is clicked inside an artifact header */
  onToggleExpand?: () => void;
  // Called when the user navigates within an artifact (tab/index change) so the
  //  parent can persist the view state per artifact. Works for both DATAFRAME_ANALYSIS
  //  and WORKFLOW_RUN tabs — callers pass either selectedResultPillName or selectedOutputPillName
  //  and updateViewState normalises both to the shared selectedPillName.
  onArtifactTabViewChange?: (
    orderIndex: number,
    view: {
      activeTab: string;
      selectedResultPillName?: string;
      selectedOutputPillName?: string;
      selectedInputPillName?: string;
    }
  ) => void;
  /** User's preferred timezone, forwarded to AnalysisOutputCard for timestamp formatting */
  defaultTimeZone?: string;
  /** Current chat session id — used as fallback when artifact.chat_id is not yet set (live chat) */
  chatId?: string;
  /** Analyst list, forwarded to WorkflowRunSearchCard for analyst name lookup */
  analystList?: HeaderWithStats[];
  /** Scenario jobs, forwarded to WorkflowRunSearchCard for owner ID lookup */
  scenarioJobs?: ScheduledJobProto[];
}

const ArtifactPanel: React.FC<ArtifactPanelProps> = ({
  selectedArtifacts = [],
  isLoadingArtifact = false,
  onClosePanel,
  onClearAll,
  onRemoveArtifact,
  savedJobIds = new Set(),
  onPopulateInput,
  activateArtifact,
  artifactMap,
  lookupFn,
  onArtifactClick,
  isArtifactExpanded = false,
  onToggleExpand,
  onArtifactTabViewChange,
  defaultTimeZone,
  chatId,
  analystList,
  scenarioJobs,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const activeTabKeyRef = useRef(activeTabKey);
  activeTabKeyRef.current = activeTabKey;
  const tabStripRef = useRef<HTMLDivElement>(null);
  const [browseSearch, setBrowseSearch] = useState("");
  const [browseActiveIndex, setBrowseActiveIndex] = useState(-1);
  const browseSearchInputRef = useRef<HTMLInputElement>(null);
  const browseListRef = useRef<HTMLDivElement>(null);
  const { isOpen: isBrowseOpen, onOpen: onBrowseOpen, onClose: onBrowseCloseRaw } = useDisclosure();
  const handleBrowseClose = useCallback(() => {
    setBrowseSearch("");
    setBrowseActiveIndex(-1);
    onBrowseCloseRaw();
  }, [onBrowseCloseRaw]);
  // Persists the selected run index for each ANALYSIS_OUTPUT group across tab switches.
  // Stored here (panel level) rather than in SelectedArtifactItem because a group
  // is made up of multiple items sharing one groupId — there is no single item to
  // attach the view state to. The groupId key matches the one used in analysisOutputGroups.
  const [groupRunIndices, setGroupRunIndices] = useState<Map<string, number>>(new Map());
  // Build sectioned browse menu using the shared classification from mentionUtils.
  // Each section (Tables, Charts, Code, Workflows) maps to a labeled group in the
  // dropdown. The same sections drive the @ mention menu in the chat input.
  const browseSections = useMemo(() => {
    if (!artifactMap) return [];
    return buildArtifactSections(artifactMap).map(section => ({
      ...section,
      items: section.items.map(entry => ({
        ...entry,
        onClick: () => entry.artifacts.forEach(a => onArtifactClick?.(a, entry.opts)),
      })),
    }));
  }, [artifactMap, onArtifactClick]);

  const totalBrowseCount = useMemo(() => browseSections.reduce((sum, s) => sum + s.items.length, 0), [browseSections]);

  const filteredBrowseSections = useMemo(
    () =>
      browseSections
        .map(s => ({
          ...s,
          items: browseSearch ? s.items.filter(i => labelMatchesQuery(i.label, browseSearch).matched) : s.items,
        }))
        .filter(s => s.items.length > 0),
    [browseSections, browseSearch]
  );

  const flatBrowseItems = useMemo(() => filteredBrowseSections.flatMap(s => s.items), [filteredBrowseSections]);

  // ── Build groups (ANALYSIS_OUTPUT items with shared groupId → one tab) ────
  // O(n) using a Map, not O(n²) with Array.find per item.

  const analysisOutputGroups = useMemo((): AnalysisOutputGroup[] => {
    const groupMap = new Map<string, AnalysisOutputGroup>();
    for (const item of selectedArtifacts) {
      if (
        !item.artifact ||
        artifactTypeFromJSON(item.artifact.artifact_type) !== ArtifactType.ANALYSIS_OUTPUT ||
        !item.artifact.analysis_output
      )
        continue;

      const gid = item.analysisGroupId ?? `solo-${item.orderIndex}`;
      if (!groupMap.has(gid)) {
        groupMap.set(gid, { groupId: gid, items: [], entries: [], minOrderIndex: Infinity });
      }
      const group = groupMap.get(gid)!;
      group.items.push(item);
      group.entries.push({
        output: item.artifact.analysis_output,
        onRemove: () => onRemoveArtifact?.(item.artifact!),
      });
      if (item.orderIndex < group.minOrderIndex) group.minOrderIndex = item.orderIndex;
    }
    return Array.from(groupMap.values());
  }, [selectedArtifacts, onRemoveArtifact]);

  // ── Build unified tab list ─────────────────────────────────────────────────

  const allTabs = useMemo((): ArtifactTab[] => {
    const tabs: ArtifactTab[] = [];

    // ANALYSIS_OUTPUT group tabs
    for (const group of analysisOutputGroups) {
      tabs.push({
        key: `group-${group.groupId}`,
        label: `Analysis (${group.entries.length})`,
        icon: AppIcons.search,
        sortKey: group.minOrderIndex,
        onClose: () => group.entries.forEach(e => e.onRemove()),
      });
    }

    // Non-ANALYSIS_OUTPUT tabs
    for (const item of selectedArtifacts) {
      const type = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;
      if (type === ArtifactType.ANALYSIS_OUTPUT) continue;
      tabs.push({
        key: `item-${item.orderIndex}`,
        label: getArtifactLabel(item.artifact!),
        icon: getArtifactIcon(item.artifact!),
        sortKey: item.orderIndex,
        onClose: () => item.artifact && onRemoveArtifact?.(item.artifact),
      });
    }

    // Chronological order (oldest tab on left)
    tabs.sort((a, b) => a.sortKey - b.sortKey);
    return tabs;
  }, [selectedArtifacts, analysisOutputGroups, onRemoveArtifact]);

  // ── Explicit activation signal — fired by every open-handler in useArtifactPanel ──
  // Every tab append and update goes through setActivateArtifact so there is one
  // explicit activation path rather than implicit newestOrderIndex tracking.
  useEffect(() => {
    if (!activateArtifact) return;
    const item = selectedArtifacts.find(i => i.orderIndex === activateArtifact.orderIndex);
    if (item) {
      setActiveTabKey(getArtifactTabKey(item));
    }
  }, [activateArtifact?.tick]);

  // If the active tab's artifact was removed (or all tabs cleared), fall back to the
  // rightmost remaining tab, or null when the panel is empty.
  // Reading activeTabKey via ref (not as a dep) so this effect only fires when allTabs
  // itself changes — not a second time when the setActiveTabKey call above changes the key.
  useEffect(() => {
    if (allTabs.length === 0) {
      setActiveTabKey(null);
      return;
    }
    if (!activeTabKeyRef.current) return;
    const stillExists = allTabs.some(t => t.key === activeTabKeyRef.current);
    if (!stillExists) {
      setActiveTabKey(allTabs[allTabs.length - 1]?.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTabs]);

  // Scroll the tab strip so the active tab is visible
  useEffect(() => {
    if (!activeTabKey || !tabStripRef.current) return;

    const container = tabStripRef.current;
    const activeEl = container.querySelector<HTMLElement>(`[data-artifact-tab-id="${activeTabKey}"]`);

    if (!activeEl) return;

    activeEl.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeTabKey, allTabs]);

  const hasAnything = selectedArtifacts.length > 0 || isLoadingArtifact;

  // ── Render the content for the currently active tab ───────────────────────
  const renderActiveContent = () => {
    if (!activeTabKey) return null;

    // ANALYSIS_OUTPUT group tab
    if (activeTabKey.startsWith("group-")) {
      const groupId = activeTabKey.slice("group-".length);
      const group = analysisOutputGroups.find(g => g.groupId === groupId);
      if (!group) return null;
      return (
        <Box key={groupId} width="100%" overflow="hidden" p={1}>
          <AnalysisOutputCard
            key={groupId}
            entries={group.entries}
            defaultExpanded={true}
            defaultTimeZone={defaultTimeZone}
            selectedRunIndex={groupRunIndices.get(groupId) ?? 0}
            onSelectedRunIndexChange={i => setGroupRunIndices(prev => new Map(prev).set(groupId, i))}
          />
        </Box>
      );
    }

    const orderIndex = parseInt(activeTabKey.slice("item-".length), 10);
    const item = selectedArtifacts.find(i => i.orderIndex === orderIndex);
    if (!item) return null;

    const artifactType = item.artifact ? artifactTypeFromJSON(item.artifact.artifact_type) : null;

    if (artifactType === ArtifactType.SCENARIO_JOB && item.artifact?.scenario_job) {
      const scheduleJob = item.artifact?.scenario_job;
      if (!scheduleJob) return null;
      const jobId = scheduleJob.header?.id ?? "";
      const isSavedInAnalyst = !!jobId && savedJobIds.has(jobId);
      return (
        <Box key={item.orderIndex} w="100%" h="100%" minH={0} overflow="hidden" p={1} display="flex" flexDir="column">
          <WorkflowCard
            key={item.orderIndex}
            scheduleJob={scheduleJob}
            isSavedInAnalyst={isSavedInAnalyst}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
            initialActiveTab={item.artifactView?.activeTab}
            onViewStateChange={view =>
              onArtifactTabViewChange?.(item.orderIndex, { activeTab: view.activeTab, selectedResultPillName: "" })
            }
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.KG_DATAFRAME && item.kgDataFrameData) {
      return (
        <Box key={item.orderIndex} w="100%" flex="1" minH={0} display="flex" flexDir="column" overflow="hidden" p={1}>
          <DataFrameDisplayComponent
            key={item.orderIndex}
            name={item.kgDataFrameData?.header?.name || "KG DataFrame"}
            data={item.kgDataFrameData}
            onClose={() => onRemoveArtifact?.(item.artifact!)}
            isSelected={true}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.DATAFRAME_ANALYSIS && item.artifact?.dataframe_analysis) {
      return (
        <Box key={item.orderIndex} w="100%" flex="1" minH={0} display="flex" flexDir="column" overflow="hidden" p={1}>
          <DataFrameAnalysisComponent
            key={item.orderIndex}
            data={item.artifact.dataframe_analysis}
            onClose={() => onRemoveArtifact?.(item.artifact!)}
            onPopulateInput={onPopulateInput}
            lookupFn={lookupFn}
            onOpenArtifact={onArtifactClick}
            initialActiveTab={item.artifactView?.activeTab}
            initialResultPillName={item.artifactView?.selectedPillName}
            initialInputPillName={item.artifactView?.selectedInputPillName}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
            onArtifactTabViewChange={view => onArtifactTabViewChange?.(item.orderIndex, view)}
            chatId={item.artifact.chat_id || chatId}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.FILE_ARTIFACT && item.artifact?.file_artifact) {
      return (
        <Box key={item.orderIndex} w="100%" flex="1" minH={0} display="flex" flexDir="column" overflow="hidden" p={1}>
          <FileArtifactComponent
            key={item.orderIndex}
            data={item.artifact.file_artifact}
            onClose={() => onRemoveArtifact?.(item.artifact!)}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.WORKFLOW_RUN && item.artifact?.workflow_run) {
      return (
        <Box key={item.orderIndex} w="100%" h="100%" minH={0} overflow="hidden" p={1} display="flex" flexDir="column">
          <WorkflowRunCard
            key={item.orderIndex}
            artifact={item.artifact}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
            defaultTimeZone={defaultTimeZone}
            initialActiveTab={item.artifactView?.activeTab}
            initialOutputPillName={item.artifactView?.selectedPillName}
            initialInputPillName={item.artifactView?.selectedInputPillName}
            onArtifactTabViewChange={view => onArtifactTabViewChange?.(item.orderIndex, view)}
            analystList={analystList}
            scenarioJobs={scenarioJobs}
          />
        </Box>
      );
    }

    if (artifactType === ArtifactType.WORKFLOW_RUN_SEARCH && item.artifact?.workflow_run_search) {
      return (
        <Box key={item.orderIndex} w="100%" h="100%" minH={0} overflow="hidden" p={1} display="flex" flexDir="column">
          <WorkflowRunSearchCard
            key={item.orderIndex}
            artifact={item.artifact}
            defaultTimeZone={defaultTimeZone}
            isExpanded={isArtifactExpanded}
            onExpandToggle={onToggleExpand}
            onOpenWorkflowRun={onArtifactClick}
            analystList={analystList}
            scenarioJobs={scenarioJobs}
          />
        </Box>
      );
    }

    return (
      <Box p={4}>
        <Text color="gray.500" fontSize="sm">
          Unable to display artifact
        </Text>
      </Box>
    );
  };

  return (
    <Box height="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">
      {/* ── Tab-strip Header ──────────────────────────────────────────────── */}
      <HStack
        spacing={0}
        align="center"
        px={1}
        py={0.5}
        borderBottom="1px solid"
        borderColor="gray.400"
        bg="gray.50"
        flexShrink={0}
      >
        {/* Left: browse-menu icon + label */}
        <Popover
          isOpen={isBrowseOpen}
          onOpen={onBrowseOpen}
          onClose={handleBrowseClose}
          placement="bottom-start"
          initialFocusRef={browseSearchInputRef}
          returnFocusOnClose={false}
          isLazy
        >
          <Tooltip
            label={totalBrowseCount > 0 ? "Show all artifacts in this chat" : "No artifacts in this session yet"}
            placement="left-end"
            isDisabled={isBrowseOpen}
          >
            {/* Box wrapper allows Tooltip to attach to a disabled button */}
            <Box display="inline-flex" flexShrink={0}>
              <PopoverTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  px={1}
                  py={1}
                  flexShrink={0}
                  isDisabled={totalBrowseCount === 0}
                  aria-label={`Open session artifacts menu (${totalBrowseCount} in list)`}
                  _hover={totalBrowseCount > 0 ? { bg: "purple.50" } : undefined}
                >
                  <HStack spacing={1} align="center">
                    <Icon as={AppIcons.layers} boxSize={5} color="purple.600" aria-hidden />
                    <Text
                      as="span"
                      fontSize="xs"
                      fontWeight="700"
                      color="purple.700"
                      mb={0}
                      lineHeight="1"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                      mr={1}
                    >
                      {totalBrowseCount}
                    </Text>
                  </HStack>
                </Button>
              </PopoverTrigger>
            </Box>
          </Tooltip>
          <Portal>
            <PopoverContent
              w="max-content"
              minW="240px"
              maxH="500px"
              overflowY="auto"
              shadow="lg"
              zIndex={1500}
              p={0}
              _focus={{ outline: "none" }}
            >
              <PopoverBody p={0} py={1}>
                <Box px={2} pb={2} pt={1}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={AppIcons.search} color="gray.400" boxSize={3} />
                    </InputLeftElement>
                    <Input
                      ref={browseSearchInputRef}
                      placeholder="Search artifacts…"
                      value={browseSearch}
                      onChange={e => {
                        setBrowseSearch(e.target.value);
                        setBrowseActiveIndex(-1);
                      }}
                      bg="gray.50"
                      borderColor="gray.200"
                      borderRadius="md"
                      _focus={{
                        borderColor: "purple.400",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                        bg: "white",
                      }}
                      onKeyDown={e => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          const next = Math.min(browseActiveIndex + 1, flatBrowseItems.length - 1);
                          setBrowseActiveIndex(next);
                          browseListRef.current
                            ?.querySelector<HTMLElement>(`[data-browse-idx="${next}"]`)
                            ?.scrollIntoView({ block: "nearest" });
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          // ArrowUp from the first item (or no selection) returns to no selection.
                          const prev = browseActiveIndex <= 0 ? -1 : browseActiveIndex - 1;
                          setBrowseActiveIndex(prev);
                          browseListRef.current
                            ?.querySelector<HTMLElement>(`[data-browse-idx="${prev}"]`)
                            ?.scrollIntoView({ block: "nearest" });
                        } else if (e.key === "Enter") {
                          e.preventDefault();
                          const item = flatBrowseItems[browseActiveIndex];
                          if (item) {
                            item.onClick();
                            handleBrowseClose();
                          }
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          handleBrowseClose();
                        }
                      }}
                    />
                  </InputGroup>
                </Box>
                {filteredBrowseSections.length === 0 ? (
                  <Text fontSize="sm" color="gray.400" textAlign="center" py={4} px={3} mb={0}>
                    {browseSearch ? "No matching artifacts" : "No artifacts yet"}
                  </Text>
                ) : (
                  <Box ref={browseListRef} onMouseLeave={() => setBrowseActiveIndex(-1)}>
                    {filteredBrowseSections.map((section, sIdx) => (
                      <Box key={section.id}>
                        {sIdx > 0 && <Divider my={1} />}
                        <Text
                          fontSize="xs"
                          fontWeight="800"
                          color="purple.700"
                          letterSpacing="0.08em"
                          px={3}
                          pt={1}
                          pb={0.5}
                          mb={0}
                          userSelect="none"
                        >
                          {section.label.toUpperCase()}
                        </Text>
                        {section.items.map(item => {
                          const idx = flatBrowseItems.indexOf(item);
                          const isActive = idx === browseActiveIndex;
                          return (
                            <Box
                              key={item.key}
                              data-browse-idx={idx}
                              px={3}
                              py={1}
                              cursor="pointer"
                              bg={isActive ? "purple.50" : "transparent"}
                              _hover={{ bg: "purple.50" }}
                              onMouseEnter={() => setBrowseActiveIndex(idx)}
                              onClick={() => {
                                item.onClick();
                                handleBrowseClose();
                              }}
                            >
                              <HStack spacing={2}>
                                <Icon as={item.icon} boxSize={3.5} flexShrink={0} color="gray.600" />
                                <Text fontSize="sm" fontWeight="600" color="gray.900" mb={0} noOfLines={1}>
                                  {item.label}
                                </Text>
                              </HStack>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                )}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
        {/* Middle: scrollable tabs */}
        <Box
          ref={tabStripRef as React.RefObject<HTMLDivElement>}
          flex="1"
          overflowX="auto"
          css={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
          h="100%"
        >
          <HStack spacing={1} align="flex-end" minW="min-content" py={0.5} pr={1}>
            {allTabs.map(tab => {
              const isActive = activeTabKey === tab.key;
              return (
                <HStack
                  key={tab.key}
                  role="tab"
                  data-artifact-tab-id={tab.key}
                  aria-selected={isActive}
                  maxW="240px"
                  minW={0}
                  flexShrink={0}
                  spacing={2}
                  pl={2}
                  pr={0.5}
                  py={0.5}
                  borderRadius="md"
                  bg="white"
                  borderWidth="1px"
                  borderColor={isActive ? "purple.700" : "gray.400"}
                  boxShadow={isActive ? "0 1px 2px rgba(107, 70, 193, 0.12)" : "none"}
                  cursor="pointer"
                  onClick={() => setActiveTabKey(tab.key)}
                  _hover={{
                    bg: isActive ? "purple.50" : "gray.50",
                    borderColor: isActive ? "purple.300" : "gray.300",
                  }}
                  sx={{
                    "&:hover .artifact-tab-close, &:focus-within .artifact-tab-close": {
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                  }}
                >
                  <Icon as={tab.icon} boxSize={3} color={isActive ? "purple.600" : "gray.500"} flexShrink={0} />
                  <Text
                    fontSize="xs"
                    fontWeight={isActive ? "700" : "500"}
                    color={isActive ? "purple.800" : "gray.600"}
                    mb={0}
                    flex={1}
                    minW={0}
                    noOfLines={1}
                    title={tab.label}
                    textAlign="left"
                  >
                    {tab.label}
                  </Text>
                  <IconButton
                    className="artifact-tab-close"
                    size="xs"
                    variant="ghost"
                    aria-label={`Close ${tab.label}`}
                    icon={<Icon as={AppIcons.close} boxSize={3} />}
                    color="gray.600"
                    minW="24px"
                    h="24px"
                    flexShrink={0}
                    onClick={e => {
                      e.stopPropagation();
                      tab.onClose();
                    }}
                    opacity={isActive ? 1 : 0}
                    pointerEvents={isActive ? "auto" : "none"}
                    transition="opacity 0.12s ease"
                    _hover={{ color: "gray.700", bg: "blackAlpha.100" }}
                  />
                </HStack>
              );
            })}
          </HStack>
        </Box>

        {/* Right: clear-all + collapse */}
        <HStack spacing={0} flexShrink={0} h="100%" px={1}>
          {selectedArtifacts.length > 0 && (
            <Button
              leftIcon={<Icon as={AppIcons.delete} boxSize="2.5" />}
              size="xs"
              colorScheme="red"
              color="gray.500"
              _hover={{ color: "red.500", bg: "red.50" }}
              onClick={onClearAll}
              fontSize="xs"
              iconSpacing={1}
              variant="ghost"
            >
              Clear
            </Button>
          )}
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "gray.700", bg: "gray.100" }}
            onClick={onClosePanel}
            p={1}
          >
            <Icon as={AppIcons.collapseSidebar} boxSize="5" />
          </Button>
        </HStack>
      </HStack>

      {/* ── Loading Indicator ─────────────────────────────── */}
      {isLoadingArtifact && (
        <Box
          p={2}
          position="absolute"
          top="15%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="25"
          bg="green.100"
          borderRadius={3}
          boxShadow="md"
        >
          <HStack spacing={2}>
            <Spinner size="sm" color="green.600" thickness="2px" />
            <Text mb={0} fontSize="xs" fontWeight="semibold" color="green.700">
              Loading artifact...
            </Text>
          </HStack>
        </Box>
      )}

      {/* ── Content: flex column + minH=0 so children can fill & scroll in place, not the whole bar */}
      <Box
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        p={0}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "3px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#A0AEC0" },
        }}
      >
        {!hasAnything ? (
          <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={6}>
            <Box textAlign="center">
              <Icon as={AppIcons.file} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.600" fontSize="lg" mb={2}>
                No Artifacts Selected
              </Text>
              <Text fontSize="sm" color="gray.500">
                Click on artifacts in the chat to view them here
              </Text>
            </Box>
          </Box>
        ) : (
          renderActiveContent()
        )}
      </Box>
    </Box>
  );
};

export default ArtifactPanel;
